import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X, RotateCcw } from 'lucide-react';
import { getPayrollDetail, updatePayrollBreakdown, recalculatePayroll } from '../services/payrollService';
import { formatCurrency } from './PayslipDocument';

// The deductions row that carries the attendance-based loss of pay (same label the backend uses).
const LOP_ROW_LABEL = 'Other Deductions';

const inputClass =
  'h-9 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30 disabled:bg-muted/40';

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

let nextRowId = 1;
const toRows = (items) => (items || []).map((item) => ({
  id: nextRowId++,
  label: item.label || '',
  amount: item.amount === null || item.amount === undefined ? '' : String(item.amount),
}));
const isLopRow = (row) => row.label.trim().toLowerCase() === LOP_ROW_LABEL.toLowerCase();

/**
 * Lets the admin review and edit one employee's processed pay: working days, LOP days, variable
 * pay and every earnings/deductions row. With "Auto-calculate" on, the Other Deductions row
 * follows total earnings / working days x LOP days as the other fields change.
 */
function PayrollEditModal({ payrollId, employeeName, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [workingDays, setWorkingDays] = useState('');
  const [lop, setLop] = useState('');
  const [variablePay, setVariablePay] = useState('');
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [autoLop, setAutoLop] = useState(true);

  const applyDetail = (data) => {
    setDetail(data);
    setWorkingDays(String(data.workingDays ?? 0));
    setLop(String(data.lop ?? 0));
    setVariablePay(String(data.variablePay ?? 0));
    setEarnings(toRows(data.earnings));
    setDeductions(toRows(data.deductions));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getPayrollDetail(payrollId);
        if (!cancelled) applyDetail(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load payroll details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [payrollId]);

  const isCredited = ['amount credited', 'credited', 'amount_credited', 'paid', 'payment credited']
    .includes(String(detail?.creditStatus || '').trim().toLowerCase());
  const locked = isCredited || saving;

  const workingNumber = Number(workingDays) || 0;
  const lopNumber = Number(lop) || 0;
  const totalEarnings = earnings.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const autoLopAmount = workingNumber > 0 ? round2((totalEarnings / workingNumber) * Math.min(lopNumber, workingNumber)) : 0;

  // Deductions as they'll be saved - the LOP row swapped for the auto amount when that's on.
  const effectiveDeductions = useMemo(() => {
    if (!autoLop) return deductions;
    const hasLopRow = deductions.some(isLopRow);
    const rows = deductions.map((row) => (isLopRow(row) ? { ...row, amount: String(autoLopAmount) } : row));
    return hasLopRow || autoLopAmount === 0 ? rows : [...rows, { id: 'auto-lop', label: LOP_ROW_LABEL, amount: String(autoLopAmount) }];
  }, [autoLop, deductions, autoLopAmount]);

  const totalDeductions = effectiveDeductions.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const netPay = Math.max(totalEarnings + (Number(variablePay) || 0) - totalDeductions, 0);

  const updateRow = (setRows) => (id, field, value) =>
    setRows((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  const removeRow = (setRows) => (id) => setRows((rows) => rows.filter((row) => row.id !== id));
  const addRow = (setRows) => () => setRows((rows) => [...rows, { id: nextRowId++, label: '', amount: '' }]);

  const handleSave = async () => {
    setError('');
    if (workingNumber <= 0) {
      setError('Working days must be greater than 0.');
      return;
    }
    if (lopNumber < 0 || lopNumber > workingNumber || !Number.isInteger(lopNumber)) {
      setError('LOP days must be a whole number between 0 and the working days.');
      return;
    }
    const filled = (rows) => rows.filter((row) => row.label.trim() || row.amount !== '');
    const earningRows = filled(earnings);
    const deductionRows = filled(effectiveDeductions);
    const invalid = [...earningRows, ...deductionRows].find(
      (row) => !row.label.trim() || row.amount === '' || Number.isNaN(Number(row.amount)) || Number(row.amount) < 0
    );
    if (invalid) {
      setError('Every earning and deduction needs a name and an amount of 0 or more.');
      return;
    }
    if (earningRows.length === 0) {
      setError('Add at least one earning.');
      return;
    }

    setSaving(true);
    try {
      const toPayload = (rows) => rows.map((row) => ({ label: row.label.trim(), amount: round2(row.amount) }));
      const saved = await updatePayrollBreakdown(payrollId, {
        workingDays: workingNumber,
        lop: lopNumber,
        variablePay: round2(variablePay),
        earnings: toPayload(earningRows),
        deductions: toPayload(deductionRows),
      });
      onSaved(saved, 'Pay updated successfully.');
    } catch (err) {
      setError(err.message || 'Failed to save payroll changes');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    if (!window.confirm('Recalculate this pay from attendance? Any manual changes will be discarded.')) return;
    setError('');
    setSaving(true);
    try {
      const data = await recalculatePayroll(payrollId);
      applyDetail(data);
      setAutoLop(true);
      onSaved(data, 'Pay recalculated from attendance.', { keepOpen: true });
    } catch (err) {
      setError(err.message || 'Failed to recalculate payroll');
    } finally {
      setSaving(false);
    }
  };

  const renderRows = (rows, setRows, isDeductions) => (
    <div className="flex flex-col gap-2">
      {rows.length === 0 && <p className="text-sm text-muted-foreground">No rows yet.</p>}
      {rows.map((row) => {
        const autoRow = isDeductions && autoLop && isLopRow(row);
        return (
          <div key={row.id} className="flex items-center gap-2">
            <input
              type="text"
              value={row.label}
              disabled={locked || autoRow}
              onChange={(e) => updateRow(setRows)(row.id, 'label', e.target.value)}
              placeholder={isDeductions ? 'e.g. TDS' : 'e.g. Bonus'}
              className={`${inputClass} min-w-0 flex-1`}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              value={autoRow ? autoLopAmount : row.amount}
              disabled={locked || autoRow}
              title={autoRow ? 'Auto-calculated from LOP days' : undefined}
              onChange={(e) => updateRow(setRows)(row.id, 'amount', e.target.value)}
              className={`${inputClass} w-32 shrink-0`}
            />
            <button
              type="button"
              onClick={() => removeRow(setRows)(row.id)}
              disabled={locked || autoRow}
              title="Remove"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-[#b91c1c] disabled:opacity-40"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl rounded-xl border border-border/80 bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border/80 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">Edit Pay - {employeeName}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Calculated from attendance: each absent working day is deducted under &ldquo;{LOP_ROW_LABEL}&rdquo;.
              {detail?.manuallyAdjusted && ' This pay has been manually adjusted.'}
            </p>
          </div>
          <button type="button" onClick={onClose} title="Close" className="rounded-md p-1.5 text-muted-foreground hover:bg-muted">
            <X className="size-4" />
          </button>
        </div>

        <div className="flex flex-col gap-5 px-5 py-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading pay details...</p>
          ) : !detail ? (
            <p className="text-sm text-[#b91c1c]">{error || 'Payroll record not found.'}</p>
          ) : (
            <>
              {isCredited && (
                <div className="rounded-lg border border-[#fde68a] bg-[#fffbeb] px-3 py-2 text-sm text-[#b45309]">
                  This payroll is already credited. Cancel the credited status on the report to edit the pay.
                </div>
              )}

              <div className="flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                  Working Days
                  <input type="number" min="1" value={workingDays} disabled={locked} onChange={(e) => setWorkingDays(e.target.value)} className={`${inputClass} w-28`} />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                  LOP Days
                  <input type="number" min="0" step="1" value={lop} disabled={locked} onChange={(e) => setLop(e.target.value)} className={`${inputClass} w-28`} />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                  Variable Pay
                  <input type="number" min="0" step="0.01" value={variablePay} disabled={locked} onChange={(e) => setVariablePay(e.target.value)} className={`${inputClass} w-32`} />
                </label>
                <label className="flex h-9 items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={autoLop}
                    disabled={locked}
                    onChange={(e) => setAutoLop(e.target.checked)}
                    className="size-4 rounded border-border accent-client"
                  />
                  Auto-calculate LOP deduction
                </label>
                <span className="text-sm text-muted-foreground">
                  Payable days: <strong className="text-foreground">{Math.max(workingNumber - lopNumber, 0)}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <section className="flex flex-col gap-3 rounded-xl border border-border/80 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground">Earnings</h4>
                    <button
                      type="button"
                      onClick={addRow(setEarnings)}
                      disabled={locked}
                      className="inline-flex h-8 items-center gap-1 rounded-lg border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                    >
                      <Plus className="size-3.5" /> Add Earning
                    </button>
                  </div>
                  {renderRows(earnings, setEarnings, false)}
                  <div className="mt-auto flex justify-between border-t border-border/80 pt-3 text-sm font-semibold text-foreground">
                    <span>Total Earnings</span>
                    <span>{formatCurrency(totalEarnings)}</span>
                  </div>
                </section>

                <section className="flex flex-col gap-3 rounded-xl border border-border/80 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground">Deductions</h4>
                    <button
                      type="button"
                      onClick={addRow(setDeductions)}
                      disabled={locked}
                      className="inline-flex h-8 items-center gap-1 rounded-lg border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                    >
                      <Plus className="size-3.5" /> Add Deduction
                    </button>
                  </div>
                  {renderRows(deductions, setDeductions, true)}
                  {autoLop && !deductions.some(isLopRow) && autoLopAmount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {LOP_ROW_LABEL} ({formatCurrency(autoLopAmount)}) will be added for {lopNumber} LOP day{lopNumber === 1 ? '' : 's'}.
                    </p>
                  )}
                  <div className="mt-auto flex justify-between border-t border-border/80 pt-3 text-sm font-semibold text-foreground">
                    <span>Total Deductions</span>
                    <span>{formatCurrency(totalDeductions)}</span>
                  </div>
                </section>
              </div>

              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border/80 bg-muted/30 px-4 py-3 text-sm text-foreground">
                <span>Earnings {formatCurrency(totalEarnings)}</span>
                <span className="text-muted-foreground">+ Variable {formatCurrency(Number(variablePay) || 0)}</span>
                <span className="text-muted-foreground">&minus; Deductions {formatCurrency(totalDeductions)}</span>
                <span className="ml-auto text-base">
                  Net Pay: <strong>{formatCurrency(netPay)}</strong>
                </span>
              </div>

              {error && <p className="text-sm text-[#b91c1c]">{error}</p>}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/80 px-5 py-4">
          <button
            type="button"
            onClick={handleRecalculate}
            disabled={loading || !detail || locked}
            className="mr-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            <RotateCcw className="size-4" /> Recalculate from attendance
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !detail || locked}
            className="h-9 rounded-lg bg-client px-4 text-sm font-medium text-client-foreground hover:bg-client/90 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Pay'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PayrollEditModal;
